import * as XLSX from 'xlsx';
import type { Transaction } from '@/types';

/**
 * Export transactions to CSV format
 */
export function exportToCSV(transactions: Transaction[], filename: string = 'transactions.csv') {
  if (transactions.length === 0) {
    alert('No transactions to export');
    return;
  }

  // Create CSV headers
  const headers = ['Date', 'Type', 'Name', 'Category', 'Amount', 'Notes'];
  
  // Create CSV rows
  const rows = transactions.map(t => [
    t.date.toLocaleDateString(),
    t.type === 'gain' ? 'Income' : 'Expense',
    t.name,
    t.categoryId,
    t.amount.toString(),
    t.notes || ''
  ]);

  // Combine headers and rows
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  downloadFile(blob, filename);
}

/**
 * Export transactions to Excel format
 */
export function exportToExcel(transactions: Transaction[], filename: string = 'transactions.xlsx') {
  if (transactions.length === 0) {
    alert('No transactions to export');
    return;
  }

  // Prepare data for Excel
  const data = transactions.map(t => ({
    Date: t.date.toLocaleDateString(),
    Type: t.type === 'gain' ? 'Income' : 'Expense',
    Name: t.name,
    Category: t.categoryId,
    Amount: t.amount,
    Notes: t.notes || ''
  }));

  // Create workbook and worksheet
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(data);

  // Set column widths
  ws['!cols'] = [
    { wch: 12 }, // Date
    { wch: 10 }, // Type
    { wch: 25 }, // Name
    { wch: 20 }, // Category
    { wch: 12 }, // Amount
    { wch: 30 }, // Notes
  ];

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, 'Transactions');

  // Generate Excel file and download
  XLSX.writeFile(wb, filename);
}

/**
 * Export year summary with all months to Excel
 */
export function exportYearSummaryToExcel(
  monthlyData: Array<{
    month: string;
    salary: number;
    totalGains: number;
    totalExpenses: number;
    savings: number;
  }>,
  year: number,
  filename: string = `year-summary-${year}.xlsx`
) {
  if (monthlyData.length === 0) {
    alert('No data to export');
    return;
  }

  // Prepare summary data
  const summaryData = monthlyData.map(m => ({
    Month: m.month,
    Salary: m.salary,
    'Total Income': m.totalGains,
    'Total Expenses': m.totalExpenses,
    Savings: m.savings,
  }));

  // Calculate totals
  const totals = {
    Month: 'TOTAL',
    Salary: monthlyData.reduce((sum, m) => sum + m.salary, 0),
    'Total Income': monthlyData.reduce((sum, m) => sum + m.totalGains, 0),
    'Total Expenses': monthlyData.reduce((sum, m) => sum + m.totalExpenses, 0),
    Savings: monthlyData.reduce((sum, m) => sum + m.savings, 0),
  };

  summaryData.push(totals);

  // Create workbook and worksheet
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(summaryData);

  // Set column widths
  ws['!cols'] = [
    { wch: 10 }, // Month
    { wch: 15 }, // Salary
    { wch: 15 }, // Total Income
    { wch: 15 }, // Total Expenses
    { wch: 15 }, // Savings
  ];

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, `Summary ${year}`);

  // Generate Excel file and download
  XLSX.writeFile(wb, filename);
}

/**
 * Export complete financial report (summary + all transactions) to Excel
 */
export function exportCompleteReport(
  monthlyData: Array<{
    month: string;
    salary: number;
    totalGains: number;
    totalExpenses: number;
    savings: number;
  }>,
  transactions: Transaction[],
  year: number,
  filename: string = `financial-report-${year}.xlsx`
) {
  const wb = XLSX.utils.book_new();

  // Sheet 1: Summary
  const summaryData = monthlyData.map(m => ({
    Month: m.month,
    Salary: m.salary,
    'Total Income': m.totalGains,
    'Total Expenses': m.totalExpenses,
    Savings: m.savings,
  }));

  const totals = {
    Month: 'TOTAL',
    Salary: monthlyData.reduce((sum, m) => sum + m.salary, 0),
    'Total Income': monthlyData.reduce((sum, m) => sum + m.totalGains, 0),
    'Total Expenses': monthlyData.reduce((sum, m) => sum + m.totalExpenses, 0),
    Savings: monthlyData.reduce((sum, m) => sum + m.savings, 0),
  };

  summaryData.push(totals);

  const ws1 = XLSX.utils.json_to_sheet(summaryData);
  ws1['!cols'] = [
    { wch: 10 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
  ];
  XLSX.utils.book_append_sheet(wb, ws1, 'Summary');

  // Sheet 2: All Transactions
  if (transactions.length > 0) {
    const transactionsData = transactions.map(t => ({
      Date: t.date.toLocaleDateString(),
      Type: t.type === 'gain' ? 'Income' : 'Expense',
      Name: t.name,
      Category: t.categoryId,
      Amount: t.amount,
      Notes: t.notes || ''
    }));

    const ws2 = XLSX.utils.json_to_sheet(transactionsData);
    ws2['!cols'] = [
      { wch: 12 },
      { wch: 10 },
      { wch: 25 },
      { wch: 20 },
      { wch: 12 },
      { wch: 30 },
    ];
    XLSX.utils.book_append_sheet(wb, ws2, 'Transactions');
  }

  // Generate Excel file and download
  XLSX.writeFile(wb, filename);
}

/**
 * Helper function to download a blob as a file
 */
function downloadFile(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}